import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuthStore, type UserRole } from '@/stores/authStore';
import { SESSION_EXPIRED_EVENT } from '@/lib/api';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
}

/**
 * ProtectedRoute — redirects unauthenticated users to /login
 * Optionally restricts to specific roles (returns 403 page if access denied)
 */
export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  // Not authenticated — redirect to login with return URL
  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname, message: 'Please sign in to continue' }}
        replace
      />
    );
  }

  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Role restriction check
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-center p-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-danger-soft text-danger">
          <ShieldAlert className="h-8 w-8" strokeWidth={2} />
        </div>
        <h1 className="text-2xl font-bold">Access Denied</h1>
        <p className="text-text-2">You don&apos;t have permission to view this page.</p>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * SessionWatcher — redirects to /login whenever a 401 is detected.
 * Listens to two signals:
 *   1. The rhpc:session-expired event fired by the api client on any 401 response
 *   2. A periodic /auth/me poll as a background safety net
 * Must be mounted inside BrowserRouter.
 */
export function SessionWatcher({ children }: { children: ReactNode }) {
  const { isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) return;

    const handleExpiry = async () => {
      await logout();
      navigate('/login', {
        replace: true,
        state: { message: 'Your session has expired. Please sign in again.' },
      });
    };

    // Signal from api client — any 401 from any request
    window.addEventListener(SESSION_EXPIRED_EVENT, handleExpiry);

    // Periodic poll as a safety net (every 5 minutes)
    const interval = setInterval(async () => {
      try {
        const response = await fetch('/api/v1/auth/me', { credentials: 'include' });
        if (response.status === 401) {
          void handleExpiry();
        }
      } catch {
        // Network error — don't log out
      }
    }, 5 * 60 * 1000);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleExpiry);
      clearInterval(interval);
    };
  }, [isAuthenticated, logout, navigate]);

  return <>{children}</>;
}
